/** Geographic helpers for GPS radius warnings (MOB-005) and anomaly checks (MOB-006). */

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_FT = 20_902_231;

export function distanceFeet(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_FT * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface GpsCheck {
  distanceFeet: number | null;
  outsideRadius: boolean;
  message: string | null;
}

/** Outside radius warns but never blocks (MOB-005). */
export function gpsRadiusCheck(attemptGps: LatLng | null, addressPin: LatLng | null, radiusFeet: number): GpsCheck {
  if (!attemptGps) return { distanceFeet: null, outsideRadius: false, message: 'No GPS fix recorded for this attempt.' };
  if (!addressPin) return { distanceFeet: null, outsideRadius: false, message: 'Address has no map pin; distance not checked.' };
  const d = Math.round(distanceFeet(attemptGps, addressPin));
  const outside = d > radiusFeet;
  return {
    distanceFeet: d,
    outsideRadius: outside,
    message: outside ? `Attempt recorded ${d.toLocaleString('en-US')} ft from the address (limit ${radiusFeet} ft).` : null,
  };
}

/**
 * MOB-006 anomaly: several attempts at far-apart addresses recorded from
 * nearly the same device location within a short period.
 */
export function detectGpsAnomaly(
  recent: { attemptGps: LatLng; addressPin: LatLng | null; at: Date }[],
  candidate: { attemptGps: LatLng; addressPin: LatLng | null; at: Date },
  opts = { windowMinutes: 30, sameSpotFeet: 300, distinctAddressFeet: 2640, minCount: 3 },
): string | null {
  if (!candidate.addressPin) return null;
  const windowStart = +candidate.at - opts.windowMinutes * 60_000;
  const cluster = recent.filter(
    (r) =>
      +r.at >= windowStart &&
      r.addressPin &&
      distanceFeet(r.attemptGps, candidate.attemptGps) <= opts.sameSpotFeet &&
      distanceFeet(r.addressPin, candidate.addressPin!) >= opts.distinctAddressFeet,
  );
  if (cluster.length + 1 >= opts.minCount) {
    return `${cluster.length + 1} attempts at addresses ≥${opts.distinctAddressFeet} ft apart were recorded from the same location within ${opts.windowMinutes} minutes.`;
  }
  return null;
}
